import React,{useEffect,useRef,useState} from 'react';
import jsQR from 'jsqr';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiCamera,FiX,FiAlertCircle,FiLoader}=FiIcons;

const PickupQrScanner=({onScan,onClose})=>{
  const videoRef=useRef(null);
  const canvasRef=useRef(null);
  const streamRef=useRef(null);
  const frameRef=useRef(null);
  const [error,setError]=useState('');
  const [starting,setStarting]=useState(true);

  useEffect(()=>{
    let active=true;

    const stopCamera=()=>{
      if (frameRef.current) {
        window.cancelAnimationFrame(frameRef.current);
      }
      streamRef.current?.getTracks().forEach((track)=>track.stop());
    };

    const startCamera=async()=>{
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Camera access is not supported in this browser.');
        setStarting(false);
        return;
      }

      try {
        const stream=await navigator.mediaDevices.getUserMedia({
          video:{facingMode:{ideal:'environment'}},
          audio:false
        });

        if (!active) {
          stream.getTracks().forEach((track)=>track.stop());
          return;
        }

        streamRef.current=stream;
        videoRef.current.srcObject=stream;
        await videoRef.current.play();
        setStarting(false);

        const detector='BarcodeDetector' in window
          ? new window.BarcodeDetector({formats:['qr_code']})
          : null;
        const context=canvasRef.current.getContext('2d',{willReadFrequently:true});

        const scan=async()=>{
          if (!active || !videoRef.current) return;

          try {
            let value='';

            if (detector) {
              const results=await detector.detect(videoRef.current);
              value=results?.[0]?.rawValue || '';
            } else if (videoRef.current.readyState>=2) {
              const video=videoRef.current;
              canvasRef.current.width=video.videoWidth;
              canvasRef.current.height=video.videoHeight;
              context.drawImage(
                video,
                0,
                0,
                canvasRef.current.width,
                canvasRef.current.height
              );
              const imageData=context.getImageData(
                0,
                0,
                canvasRef.current.width,
                canvasRef.current.height
              );
              value=jsQR(
                imageData.data,
                imageData.width,
                imageData.height
              )?.data || '';
            }

            if (value) {
              stopCamera();
              onScan(value);
              return;
            }
          } catch (scanError) {
            console.error('QR scan failed:',scanError);
          }

          frameRef.current=window.requestAnimationFrame(scan);
        };

        frameRef.current=window.requestAnimationFrame(scan);
      } catch (cameraError) {
        setError(cameraError.message || 'Camera access was denied or unavailable.');
        setStarting(false);
      }
    };

    startCamera();

    return()=>{
      active=false;
      stopCamera();
    };
  },[onScan]);

  return(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-md">
      <div className="w-full max-w-lg overflow-hidden rounded-[2rem] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 p-5">
          <div>
            <h3 className="text-lg font-black text-slate-900">
              Scan Buyer QR Code
            </h3>
            <p className="mt-1 text-[10px] font-black uppercase tracking-widest text-indigo-500">
              Seller camera verification
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-3 text-slate-400 hover:bg-slate-50 hover:text-slate-900"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="relative aspect-square bg-slate-950">
          <video
            ref={videoRef}
            muted
            playsInline
            className="h-full w-full object-cover"
          />
          <canvas ref={canvasRef} className="hidden" />

          {!starting && !error && (
            <div className="pointer-events-none absolute inset-12 rounded-[2rem] border-4 border-white/80 shadow-[0_0_0_999px_rgba(15,23,42,0.35)]" />
          )}

          {starting && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
              <SafeIcon icon={FiLoader} className="mb-3 animate-spin text-3xl" />
              <p className="text-[10px] font-black uppercase tracking-widest">
                Starting camera
              </p>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center text-white">
              <SafeIcon icon={FiAlertCircle} className="mb-4 text-3xl text-amber-300" />
              <p className="text-sm font-bold leading-relaxed">{error}</p>
            </div>
          )}
        </div>

        <div className="p-5">
          <p className="text-center text-xs font-medium leading-relaxed text-slate-500">
            Ask the buyer to display the one-time QR code. Keep the code visible inside the frame until it is detected.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PickupQrScanner;